import { SiteLogo } from "@/components/shared/SiteLogo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useChatNotifications } from "@/hooks/useChatNotifications";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  FileText,
  Megaphone,
  LogOut,
  Menu,
  X,
  Home,
  ChevronRight,
  Shield,
  Store,
  MapPin,
  Search,
  PenSquare,
  Image,
  UserCog,
  Package,
  ShoppingCart,
  BookOpen,
  GraduationCap,
  ClipboardList,
  MessageSquare,
  Tag,
  FileBarChart,
  Settings,
  BarChart3,
} from "lucide-react";
import { useState } from "react";
import { DealerPortalDialog } from "@/components/shared/DealerPortalDialog";
import { SETTINGS_TAB_AREAS, areaForPath, useAdminAccess } from "@/lib/adminAccess";
import { useEffect } from "react";

const sidebarLinks = [
  { name: "Dashboard", path: "/admin", icon: LayoutDashboard, permKey: "dashboard" },
  { name: "Leads", path: "/admin/leads", icon: FileText, permKey: "leads" },
  { name: "Applications", path: "/admin/applications", icon: UserPlus, permKey: "applications" },
  { name: "Dealers", path: "/admin/dealers", icon: Users, permKey: "dealers" },
  { name: "Announcements", path: "/admin/announcements", icon: Megaphone, permKey: "announcements" },
  { name: "Locations", path: "/admin/locations", icon: MapPin, permKey: "locations" },
  { name: "Dealer Sales", path: "/admin/verizon-sales", icon: BarChart3, permKey: "dealer_sales" },
  { name: "Blog", path: "/admin/blog", icon: PenSquare, permKey: "blog" },
  { name: "Gallery", path: "/admin/gallery", icon: Image, permKey: "gallery" },
  { name: "Knowledge Base", path: "/admin/knowledge-base", icon: BookOpen, permKey: "knowledge_base" },
  { name: "Training", path: "/admin/training", icon: GraduationCap, permKey: "training" },
  { name: "Inventory", path: "/admin/inventory", icon: Package, permKey: "inventory" },
  { name: "Chat", path: "/admin/chat", icon: MessageSquare, permKey: "chat" },
  { name: "Deals", path: "/admin/deals", icon: Tag, permKey: "deals" },
  { name: "Reports", path: "/admin/reports", icon: FileBarChart, permKey: ["reports", "audit_log"] },
  { name: "Settings", path: "/admin/settings", icon: Settings, permKey: Object.values(SETTINGS_TAB_AREAS) },
];

export const AdminLayout = ({ children, title, breadcrumb }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [portalOpen, setPortalOpen] = useState(false);
  useChatNotifications();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  // Staff only see menu items they have View or Manage on; pages they can't open redirect
  const { can } = useAdminAccess();
  const visibleLinks = sidebarLinks.filter((l) => [].concat(l.permKey).some((k) => can(k)));
  const path = location.pathname;
  const hub = path === "/admin/settings" || path === "/admin/reports";
  const area = hub ? null : areaForPath(path);
  const blocked = visibleLinks.length > 0 && ((area && !can(area)) || (hub && !visibleLinks.some((l) => l.path === path)));
  const viewOnly = area && area !== "dealer_sales" && can(area) && !can(area, "manage");
  useEffect(() => {
    if (blocked) navigate(visibleLinks[0].path, { replace: true });
  }, [blocked, navigate, visibleLinks]);
  if (visibleLinks.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="admin-no-access">
        <div className="text-center space-y-3">
          <Shield className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="font-heading text-navy text-lg">Your login doesn't have access to any admin pages yet.</p>
          <p className="text-sm text-slate-500">Ask an admin to give you permissions.</p>
          <Button onClick={handleLogout} data-testid="admin-no-access-logout">Log out</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-navy-dark h-14 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md text-white hover:bg-white/10"
          data-testid="admin-mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-teal" />
          <span className="font-heading font-bold text-white">Admin Portal</span>
        </div>
        <div className="w-10" />
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-navy-dark z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-4 py-3 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg p-2">
                <SiteLogo className="h-14 w-auto" testId="admin-sidebar-logo" />
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto scrollbar-thin">
            <p className="label-style text-slate-500 mb-1.5 px-1">Admin Portal</p>
            {visibleLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${link.name.toLowerCase()}`}
                  className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md transition-colors ${
                    isActive(link.path)
                      ? "bg-teal text-white"
                      : "text-slate-400 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-body text-sm">{link.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="px-3 py-2.5 border-t border-white/10">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-accent-red/20 flex items-center justify-center">
                <Shield className="w-4 h-4 text-accent-red" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-slate-500 text-xs">{user?.super_admin ? "Super Admin" : user?.role === "staff" ? "Staff" : "Administrator"}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/" className="flex-1 min-w-0">
                <Button variant="outline" size="sm" className="w-full border-white/20 text-white hover:bg-white/10 h-8 text-xs">
                  <Home className="w-3.5 h-3.5 mr-1" />
                  Home
                </Button>
              </Link>
              {can("dealer_portal") && <Button variant="outline" size="sm" onClick={() => setPortalOpen(true)} data-testid="admin-dealer-portal-btn"
                className="flex-1 min-w-0 px-2 border-white/20 text-white hover:bg-white/10 h-8 text-xs">
                <Store className="w-3.5 h-3.5 mr-1 shrink-0" /> Portal
              </Button>}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="shrink-0 px-2 border-white/20 text-white hover:bg-white/10 h-8"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      <DealerPortalDialog open={portalOpen} onOpenChange={setPortalOpen} />

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        <main className="min-h-screen pt-14 lg:pt-0">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
            {breadcrumb && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Link to="/admin" className="hover:text-teal">Dashboard</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-slate-700">{breadcrumb}</span>
              </div>
            )}
            <h1 className="font-heading font-bold text-xl lg:text-2xl text-navy" data-testid="admin-page-title">
              {title}
            </h1>
          </div>

          {/* Page content */}
          <div className="p-4 lg:p-8">
            {viewOnly && (
              <div className="mb-4 px-4 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800" data-testid="admin-view-only-banner">
                View only: you can look at this page, but adding, editing and deleting are turned off for your login.
              </div>
            )}
            {!blocked && children}
          </div>
        </main>
      </div>
    </div>
  );
};
